import { hasRoleAtLeast, ROLE, type Role } from '../domain/enums';
import { GRANT } from '../domain/grants';

export type RoleAccess = 'admin-only' | 'admin-or-operator' | 'any-logged-in';
export type RouteAccess = RoleAccess | `granted:${string}`;
const MIN_ROLE: Record<Exclude<RoleAccess, 'any-logged-in'>, Role> = {
  'admin-only': ROLE.ADMIN,
  'admin-or-operator': ROLE.OPERATOR,
};
const GRANTED_BIT: Record<`granted:${string}`, number> = {
  'granted:fxQuotes': GRANT.fxQuotes,
  'granted:userManagement': GRANT.userManagement,
};

/** 判定入参：角色 + 授权位快照（登录 / 刷新 token 时更新）。 */
export interface AccessSubject {
  role: Role | undefined;
  grantsMask: number;
}

export function hasRoleAccess(role: Role | undefined, access: RoleAccess): boolean {
  if (role === undefined) return false;
  if (access === 'any-logged-in') return true;
  return hasRoleAtLeast(role, MIN_ROLE[access]);
}

/**
 * 路由 / 菜单的统一判定：角色三档 + 授权位档（backend
 * `auth/dependencies.py` 的 `fx_quotes_actor` / `user_management_actor` 同款
 * 口径——admin 恒过；operator 看位；USER 行带位也不过）。
 */
export function hasAccess(subject: AccessSubject, access: RouteAccess): boolean {
  if (subject.role === undefined) return false;
  if (access.startsWith('granted:')) {
    if (subject.role === ROLE.ADMIN) return true;
    const bit = (GRANTED_BIT as Record<string, number>)[access] ?? 0;
    return subject.role === ROLE.OPERATOR && (subject.grantsMask & bit) !== 0;
  }
  return hasRoleAccess(subject.role, access as RoleAccess);
}

/** Me 行 → 判定入参（缺 grants_mask 的旧档案按 0 处理）。 */
export function subjectOf(me: { role?: Role; grants_mask?: number } | null): AccessSubject {
  return { role: me?.role, grantsMask: me?.grants_mask ?? 0 };
}

export function isStaff(role: Role | undefined): boolean {
  return hasRoleAccess(role, 'admin-or-operator');
}

export function isAdmin(role: Role | undefined): boolean {
  return hasRoleAccess(role, 'admin-only');
}
